import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth';
import { toast } from 'sonner';
import { ConsultoresService } from '../services/consultores.service';
import { unifiedServices } from '@/services/unifiedService';
import { isSupabaseMode } from '@/config/dataSource';
import { Consultor, ConsultorFormData } from '../types/consultor';
import { api } from '@/services/api';

interface Tenant {
  id: number;
  nome: string;
}

export function useConsultores() {
  const { profile } = useAuth();
  const [consultores, setConsultores] = useState<Consultor[]>([]);
  const [supervisores, setSupervisores] = useState<Consultor[]>([]);
  const [lideres, setLideres] = useState<Consultor[]>([]);
  const [filteredConsultores, setFilteredConsultores] = useState<Consultor[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [tenantsReady, setTenantsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("todos");
  const isSuperAdmin = profile?.role === 'SUPER_ADMIN';

  // Carrega as empresas disponíveis para o superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    } else if (profile?.tenant_id) {
      setSelectedTenant(String(profile.tenant_id));
      setTenantsReady(true);
    }
  }, [isSuperAdmin, profile?.tenant_id]);

  // Atualiza a listagem quando mudar o tenant selecionado
  useEffect(() => {
    if (!profile || !tenantsReady) return;
    // Super Admin: selectedTenant null = todas as empresas
    if (!isSuperAdmin && !selectedTenant) return;
    fetchConsultores();
  }, [selectedTenant, isSuperAdmin, profile, tenantsReady]);

  // Carrega os tenants disponíveis para o superadmin
  const fetchTenants = async () => {
    try {
      if (isSupabaseMode()) {
        const tenantsData = await unifiedServices.tenants.findAll();
        // Equipe / filtros operacionais: só empresas ativas
        const ativos = tenantsData.filter(
          (t: any) => t.status === 'ATIVO' || t.ativo === true
        );
        setTenants(ativos);
        // Preferir Matriz como padrão (não o tenant mais recente — ex.: Associado Itallo)
        const matriz = ativos.find(
          (t: any) => t.tipo_unidade === 'MATRIZ' || Number(t.id) === 1
        );
        setSelectedTenant(matriz ? String(matriz.id) : null);
      } else {
        const { data } = await api.get<Tenant[]>('/api/superadmin/tenants');
        const ativos = data.filter((t: any) => t.status === 'ATIVO' || t.ativo === true);
        setTenants(ativos);
        const matriz = ativos.find((t: any) => Number(t.id) === 1);
        setSelectedTenant(matriz ? String(matriz.id) : null);
      }
    } catch (error: any) {
      toast.error("Erro ao carregar empresas", {
        description: error.message
      });
    } finally {
      setTenantsReady(true);
    }
  };

  // Carrega os consultores via API
  const fetchConsultores = async () => {
    try {
      setLoading(true);
      // Super Admin: null = todas; demais roles: tenant do profile
      const tenantId = isSuperAdmin
        ? selectedTenant || undefined
        : selectedTenant || profile?.tenant_id?.toString();
      let data = await ConsultoresService.listarConsultores(tenantId);

      if (profile?.role === 'SUPERVISOR') {
        const supervisor = data.find(c => String(c.user_id) === String((profile as any).uuid || profile.id));
        if (supervisor) {
          data = data.filter(c => c.id === supervisor.id || c.supervisor_id === supervisor.id);
        } else {
          data = [];
        }
      }

      // Atualiza os consultores com suas equipes
      const consultoresAtualizados = await Promise.all(
        data.map(async consultor => {
          // Adiciona campos necessários que podem estar faltando na resposta da API
          const consultorCompleto: Consultor = {
            ...consultor,
            tipo: consultor.tipo || 'relacionador',
            status: consultor.status || 'ativo',
            supervisor_id: consultor.supervisor_id || undefined,
            tenant_id: Number(consultor.tenant_id || selectedTenant || 0),
            created_at: consultor.created_at || new Date().toISOString(),
          };

          if (consultorCompleto.tipo === 'supervisor') {
            try {
              const equipe = await ConsultoresService.listarEquipe(String(consultorCompleto.id), tenantId);

              // Completa os dados dos membros da equipe
              const lideresCompletos = equipe.lideres.map(lider => ({
                ...lider,
                tipo: 'lider' as const,
                status: 'ativo' as const,
                supervisor_id: consultorCompleto.id,
                tenant_id: consultorCompleto.tenant_id,
                created_at: new Date().toISOString(),
              }));

              const relacionadoresCompletos = equipe.relacionadores.map(relacionador => ({
                ...relacionador,
                tipo: 'relacionador' as const,
                status: 'ativo' as const,
                supervisor_id: consultorCompleto.id,
                tenant_id: consultorCompleto.tenant_id,
                created_at: new Date().toISOString(),
              }));

              return {
                ...consultorCompleto,
                lideres: lideresCompletos,
                relacionadores: relacionadoresCompletos,
              };
            } catch (error) {
              console.error('Erro ao carregar equipe do supervisor:', error);
              return consultorCompleto;
            }
          }
          return consultorCompleto;
        })
      );

      setConsultores(consultoresAtualizados);

      // Atualiza as listas de supervisores e líderes
      const supervisoresData = consultoresAtualizados.filter(c => c.tipo === 'supervisor');
      const lideresData = consultoresAtualizados.filter(c => c.tipo === 'lider');

      setSupervisores(supervisoresData);
      setLideres(lideresData);

    } catch (error: any) {
      toast.error("Erro ao carregar consultores", {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Salva um consultor (novo ou existente)
  const saveConsultor = async (data: ConsultorFormData, id?: string): Promise<{ success: boolean }> => {
    try {
      const tenantId = data.tenant_id || selectedTenant || profile?.tenant_id?.toString();

      if (!tenantId) {
        throw new Error('Tenant ID é obrigatório');
      }

      const consultorData = {
        nome: data.nome,
        codigo: data.codigo,
        email: data.email,
        telefone: data.telefone,
        cpf: data.cpf,
        tipo: data.tipo,
        supervisor_id: data.supervisor_id || undefined,
        status: data.status,
        status_cadastro: data.status_cadastro || 'PENDENTE',
        tenant_id: Number(tenantId),
      } satisfies Omit<Consultor, 'id' | 'created_at' | 'updated_at'>;

      if (id) {
        await ConsultoresService.atualizarConsultor(id, consultorData);
      } else {
        await ConsultoresService.criarConsultor(consultorData);
      }

      toast.success("Sucesso", {
        description: "Profissional salvo com sucesso!"
      });

      // Recarrega a lista de consultores
      await fetchConsultores();

      return { success: true };
    } catch (error: any) {
      toast.error("Erro", {
        description: error.message
      });
      return { success: false };
    }
  };

  // Alterna o status do consultor entre ativo e inativo
  const toggleConsultorStatus = async (consultor: Consultor) => {
    try {
      if (consultor.status === 'ativo') {
        await ConsultoresService.inativarConsultor(String(consultor.id));
      } else {
        await ConsultoresService.ativarConsultor(String(consultor.id));
      }

      toast.success("Sucesso", {
        description: `Status do profissional alterado para ${consultor.status === 'ativo' ? 'inativo' : 'ativo'}`
      });

      // Recarrega a lista de consultores
      await fetchConsultores();
    } catch (error: any) {
      toast.error("Erro", {
        description: error.message
      });
    }
  };

  // Exclui um consultor (soft delete)
  const deleteConsultor = async (consultorId: string) => {
    try {
      await ConsultoresService.excluirConsultor(consultorId);

      toast.success("Sucesso", {
        description: "Profissional excluído com sucesso!"
      });

      // Recarrega a lista de consultores
      await fetchConsultores();
    } catch (error: any) {
      toast.error("Erro", {
        description: error.message
      });
    }
  };

  // Desliga um consultor (remove da equipe e inativa)
  const desligarConsultor = async (consultorId: string) => {
    try {
      await ConsultoresService.atualizarConsultor(consultorId, {
        supervisor_id: null,
        status: 'inativo'
      });

      toast.success("Sucesso", {
        description: "Profissional desligado com sucesso!"
      });

      // Recarrega a lista de consultores
      await fetchConsultores();
    } catch (error: any) {
      toast.error("Erro", {
        description: error.message
      });
    }
  };

  // Retorna o nome do tenant pelo ID
  const getTenantName = (tenant_id: number) => {
    const tenant = tenants.find(t => t.id === tenant_id);
    return tenant?.nome || '';
  };

  // Busca supervisores disponíveis
  const getSupervisores = () => {
    return consultores.filter(c => c.tipo === 'supervisor' && c.status === 'ativo');
  };

  // Busca líderes disponíveis para um supervisor específico
  const getLideresPorSupervisor = (supervisorId: string) => {
    return consultores.filter(
      c => c.tipo === 'lider' &&
        c.status === 'ativo' &&
        c.supervisor_id === Number(supervisorId)
    );
  };

  return {
    consultores,
    supervisores,
    lideres,
    loading,
    saveConsultor,
    toggleConsultorStatus,
    deleteConsultor,
    desligarConsultor,
    getTenantName,
    tenants,
    selectedTenant,
    setSelectedTenant,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filteredConsultores,
    setFilteredConsultores,
    getSupervisores,
    getLideresPorSupervisor,
    isSuperAdmin
  };
} 